// Force TS recompile - updated keyAttributeColumnValues casing
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Button,
    CardContent,
    DialogActions,
    DialogTitle,
    Divider,
    FormControl,
    FormControlLabel,
    Grid,
    InputLabel,
    Radio,
    RadioGroup,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { Form, FormikProvider, useFormik } from 'formik';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DEFAULT_LANGUAGE, LANGUAGE_OPTIONS } from 'inside-fifa/utils/i18n';
import { LoadingButton } from '@mui/lab';
import { ColumnMapping } from './partials/column-mapping/column-mapping';
import { UpdateConfigDialogSchema } from './schema';
import { UpdateConfigDialogFormValues, UpdateConfigDialogProps } from './dto';
import { DataSourceManager } from './partials/data-source-manager/data-source-manager';
import { LanguageSwitcher } from './partials/language-switcher/language-switcher';
import { PaginationManager } from './partials/pagination-manager/pagination-manager';
import { TablePreview } from './partials/table-preview/table-preview';
import ShowFilters from '../filters/show-filters';
import getChangedValues from 'utils/getChangedValues';
import { DefaultColumnOrder } from './partials/default-column/default-column';
import { SpecialTreatmentColumn } from './partials/special-treatment-column/special-treatment-column';
import { KeyAttributeMappings } from './partials/key-attribute-mappings/key-attribute-mappings';
import { ViewMode, FilterMode } from 'inside-fifa/api/generic-tables/dto';

export const UpdateConfigDialog = ({ config, onSubmit, onCancel }: UpdateConfigDialogProps) => {
    const initialValues = useMemo(
        () => ({
            name: config.name,
            config: {
                ...config.config,
            },
            modifiedTimestamp: config.modifiedTimestamp,
        }),
        [config],
    );

    const formik = useFormik<UpdateConfigDialogFormValues>({
        initialValues,
        enableReinitialize: true,
        onSubmit: async (values) => {
            const updatedData = getChangedValues(initialValues, values, [
                'modifiedTimestamp',
                'config.filters.isSearchBoxEnabled',
            ]);
            await onSubmit?.(updatedData);
        },
        validationSchema: UpdateConfigDialogSchema,
    });

    const { touched, errors, isSubmitting, getFieldProps, dirty, values, isValid, handleSubmit } = formik;

    const handleUnload = useCallback(
        (e: BeforeUnloadEvent) => {
            e.preventDefault();
            if (isSubmitting || dirty) return;
        },
        [isSubmitting, dirty],
    );

    useEffect(() => {
        window.addEventListener('beforeunload', handleUnload);
        return () => {
            window.removeEventListener('beforeunload', handleUnload);
        };
    }, [handleUnload]);

    const [currentLanguage, setCurrentLanguage] = useState(DEFAULT_LANGUAGE);
    const firstColumnTitle = values.config.columns[0]?.columnTitle;
    const languagesUsed = useMemo(
        () => (firstColumnTitle ? Object.keys(firstColumnTitle) : [DEFAULT_LANGUAGE]),
        [firstColumnTitle],
    );

    // Track previous datasource to detect actual user-initiated changes
    const previousDataSource = useRef(config.dataSource);
    const hasInitialized = useRef(false);

    // Reset dependent fields when user changes datasource
    useEffect(() => {
        const currentDataSource = config.dataSource;
        const hasDataSourceChanged = JSON.stringify(currentDataSource) !== JSON.stringify(previousDataSource.current);

        // Only reset if:
        // 1. Component is already initialized (not first load)
        // 2. AND datasource actually changed (user actively changed it)
        if (hasInitialized.current && hasDataSourceChanged) {
            console.log('User changed datasource, resetting dependent fields...');

            setTimeout(() => {
                formik.setFieldValue('config.columnTitle', '');
                formik.setFieldValue('config.keyAttributeColumn', '');
                formik.setFieldValue('config.keyAttributeColumnValues', []);
                formik.setFieldValue('config.defaultColumnOrder.key', null);
            }, 100);
        }
        // Update tracking after checking
        previousDataSource.current = currentDataSource;

        // Mark as initialized after the first run
        if (!hasInitialized.current) {
            hasInitialized.current = true;
        }
    }, [config.dataSource, formik]);

    return (
        <FormikProvider value={formik}>
            <Form onSubmit={handleSubmit}>
                <DialogTitle>
                    <FormattedMessage id="inside-fifa.generic-tables.update-table-config" />
                </DialogTitle>
                <CardContent>
                    <Grid container spacing={3}>
                        <Grid item xs={12} rowGap={3}>
                            <Stack spacing={1}>
                                <InputLabel>
                                    <FormattedMessage id="inside-fifa.generic-tables.table-name" />
                                </InputLabel>
                                <TextField
                                    fullWidth
                                    id="name"
                                    error={touched.name && Boolean(errors.name)}
                                    helperText={touched.name && errors.name}
                                    disabled={isSubmitting}
                                    {...getFieldProps('name')}
                                />
                            </Stack>
                        </Grid>
                        <Grid item xs={12} rowGap={3}>
                            <Accordion>
                                <AccordionSummary>
                                    <FormattedMessage id="inside-fifa-generic-tables.data-source" />
                                </AccordionSummary>
                                <AccordionDetails>
                                    <DataSourceManager onSubmit={onSubmit} initialConfig={config} />
                                </AccordionDetails>
                            </Accordion>
                            <Accordion>
                                <AccordionSummary>
                                    <FormattedMessage id="inside-fifa.generic-tables.pagination" />
                                </AccordionSummary>
                                <AccordionDetails>
                                    <PaginationManager />
                                </AccordionDetails>
                            </Accordion>
                            <Accordion>
                                <AccordionSummary>
                                    <FormattedMessage id="inside-fifa.generic-tables.default-column" />
                                </AccordionSummary>
                                <AccordionDetails>
                                    <DefaultColumnOrder />
                                </AccordionDetails>
                            </Accordion>
                        </Grid>
                    </Grid>
                </CardContent>

                <Divider sx={{ my: 2 }} />

                {/* Structure Preview Section - Prominently placed first */}
                <CardContent sx={{ pb: 1 }}>
                    <Grid container spacing={3} direction="column">
                        <Grid item>
                            <Typography variant="h3" color="secondary" gutterBottom>
                                <FormattedMessage id="inside-fifa.generic-tables.structure-preview-title" />
                            </Typography>
                        </Grid>
                        <Grid item>
                            <Grid container spacing={3}>
                                <Grid item xs={12}>
                                    <Stack direction="row" spacing={2} alignItems="center">
                                        <Typography variant="h6" fontWeight="bold">
                                            <FormattedMessage id="inside-fifa.generic-tables.view-mode" />:
                                        </Typography>
                                        <FormControl component="fieldset">
                                            <RadioGroup
                                                row
                                                name="viewMode"
                                                value={values.config.viewMode}
                                                onChange={(e) => {
                                                    const value = parseInt(e.target.value, 10);
                                                    formik.setFieldValue('config.viewMode', value);
                                                }}
                                            >
                                                <FormControlLabel
                                                    value={ViewMode.Table}
                                                    control={<Radio />}
                                                    label={<FormattedMessage id="inside-fifa.generic-tables.table" />}
                                                />
                                                <FormControlLabel
                                                    value={ViewMode.Grid}
                                                    control={<Radio />}
                                                    label={<FormattedMessage id="inside-fifa.generic-tables.grid" />}
                                                />
                                            </RadioGroup>
                                        </FormControl>
                                    </Stack>
                                </Grid>
                                {values.config.viewMode === ViewMode.Grid && (
                                    <Grid item xs={12}>
                                        <Typography variant="body2" color="text.secondary">
                                            <FormattedMessage id="inside-fifa.generic-tables.structure-preview.info.grid" />
                                        </Typography>
                                    </Grid>
                                )}

                                <Grid item xs={12}>
                                    <Typography variant="h6" fontWeight="bold">
                                        <FormattedMessage id="inside-fifa.generic-tables.columns-mapping" />
                                    </Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <LanguageSwitcher
                                        currentLanguage={currentLanguage}
                                        languagesUsed={languagesUsed}
                                        onChangeLanguage={setCurrentLanguage}
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <Typography variant="h6" fontWeight="bold">
                                        <FormattedMessage
                                            id="inside-fifa.generic-tables.structure-preview"
                                            values={{
                                                language: (
                                                    <FormattedMessage
                                                        id={
                                                            LANGUAGE_OPTIONS.find(
                                                                ({ code }) => currentLanguage === code,
                                                            )?.translationKey ?? ''
                                                        }
                                                    />
                                                ),
                                            }}
                                        />
                                    </Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <ColumnMapping currentLanguage={currentLanguage} languagesUsed={languagesUsed} />
                                </Grid>
                                <Grid item xs={12}>
                                    <TablePreview currentLanguage={currentLanguage} config={values.config} />
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </CardContent>
                <Divider sx={{ my: 2 }} />

                {/* Special Treatment Column Section */}
                <CardContent sx={{ pb: 1 }}>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <Typography variant="h3" color="secondary" gutterBottom>
                                <FormattedMessage id="inside-fifa.generic-tables.special-treatment" />
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <SpecialTreatmentColumn />
                        </Grid>
                    </Grid>
                </CardContent>

                <Divider sx={{ my: 2 }} />

                {/* Key Attribute Mappings Section */}
                <CardContent sx={{ pb: 1 }}>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <Typography variant="h6" fontWeight="bold">
                                <FormattedMessage id="inside-fifa.generic-tables.key-attribute-mappings.title" />
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <KeyAttributeMappings dataSource={config.dataSource} />
                        </Grid>
                    </Grid>
                </CardContent>
                <Divider sx={{ my: 2 }} />

                {/* Filters Section */}
                <CardContent sx={{ pb: 1 }}>
                    <Grid container spacing={3} direction="column">
                        <Grid item>
                            <Typography variant="h3" color="secondary" gutterBottom>
                                <FormattedMessage id="inside-fifa.generic-tables.filter-title" />
                            </Typography>
                        </Grid>
                        <Grid item>
                            <Stack direction="row" spacing={3} alignItems="center">
                                <Typography variant="h6" fontWeight="bold">
                                    <FormattedMessage id="inside-fifa.generic-tables.filter-mode" />
                                </Typography>
                                <FormControl component="fieldset">
                                    <RadioGroup
                                        row
                                        name="filterMode"
                                        value={values.config.filterMode}
                                        onChange={(e) => {
                                            const value = parseInt(e.target.value, 10);
                                            formik.setFieldValue('config.filterMode', value);
                                        }}
                                    >
                                        <FormControlLabel
                                            value={FilterMode.Top}
                                            control={<Radio />}
                                            label={<FormattedMessage id="inside-fifa.generic-tables.top-filter" />}
                                        />
                                        <FormControlLabel
                                            value={FilterMode.Sidebar}
                                            control={<Radio />}
                                            label={<FormattedMessage id="inside-fifa.generic-tables.sidebar-filter" />}
                                        />
                                    </RadioGroup>
                                </FormControl>
                            </Stack>
                        </Grid>
                        <Grid item>
                            <Typography variant="h6" fontWeight="bold">
                                <FormattedMessage id="inside-fifa.generic-tables.filter-configuration" />
                            </Typography>
                        </Grid>
                        <Grid item>
                            <ShowFilters languagesUsed={languagesUsed} />
                        </Grid>
                    </Grid>
                </CardContent>
                <Divider sx={{ my: 2 }} />
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button color="error" onClick={onCancel} disabled={isSubmitting} size="medium">
                        <FormattedMessage id="inside-fifa.cancel" />
                    </Button>
                    <LoadingButton
                        type="submit"
                        variant="contained"
                        size="medium"
                        loading={isSubmitting}
                        disabled={!isValid || !dirty}
                    >
                        <FormattedMessage id="inside-fifa.save" />
                    </LoadingButton>
                </DialogActions>
            </Form>
        </FormikProvider>
    );
};
